import { useFormikContext } from 'formik';
import {
  describeCompression,
  describePhotoFailure,
  describePhotoRejection,
  isHeicFile,
  MAX_SOURCE_BYTES,
} from 'modules/application-link/domain/photo/photo-messages';
import { useCallback, useState } from 'react';
import { FileRejection } from 'react-dropzone';
import { toast } from 'react-toastify';
import { compressImage, CompressImageError } from 'shared/utils/image/compress-image';
import { DefaultDropzone } from 'ud-form/components/dropzoneV2';
import UDFormLabel from 'ud-form/components/label';

/**
 * Поле «Фото» с обработкой файла на клиенте.
 *
 * Своё `onDrop` вместо стандартного нужно по двум причинам:
 *
 * 1. Бэкенд ничего не сжимает, а nginx рубит запрос больше 5 МБ HTML-страницей
 *    413 — телефонное фото без обработки не доходит.
 * 2. Стандартный обработчик в `use-drop-zone-init.tsx` выбрасывает
 *    `fileRejections`, поэтому отклонённый файл (HEIC с iPhone) не даёт вообще
 *    никакой реакции: ни превью, ни сообщения.
 *
 * Пробрасывать опции можно без правок кита: `DefaultDropzone` отправляет всё
 * лишнее в `useDropzone`, а `onDrop` там объявлен до `...rest` и потому
 * переопределяется вызывающей стороной.
 */
const PhotoField = () => {
  const { setFieldValue } = useFormikContext();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleDrop = useCallback(
    async (accepted: File[], rejections: FileRejection[]) => {
      if (rejections.length > 0) {
        toast.error(describePhotoRejection(rejections[0]));
        return;
      }

      const [source] = accepted;
      if (!source) return;

      // HEIC отсекаем до чтения в память: браузер его не декодирует, и тратить
      // время на многомегабайтный файл, чтобы упасть, незачем.
      if (isHeicFile(source)) {
        toast.error(describePhotoFailure(new CompressImageError('decode-failed'), source));
        return;
      }

      setIsProcessing(true);
      try {
        const result = await compressImage(source);
        setFieldValue('photo', [result.file]);

        const message = describeCompression(result);
        if (message) toast.info(message);
      } catch (error) {
        toast.error(describePhotoFailure(error, source));
      } finally {
        setIsProcessing(false);
      }
    },
    [setFieldValue],
  );

  return (
    <div>
      <UDFormLabel className="mb-2 whitespace-pre-wrap" style={{ color: 'white' }}>
        {'Фото (не обязательно).\nБольшое фото сожмём автоматически'}
      </UDFormLabel>
      <DefaultDropzone
        name="photo"
        className="p-6"
        disabled={isProcessing}
        maxSize={MAX_SOURCE_BYTES}
        // HEIC добавлен в разрешённые намеренно: декодировать его браузер не умеет,
        // но так файл доходит до нашего обработчика и получает внятный отказ
        // вместо полной тишины.
        accept={{ 'image/heic': ['.heic'], 'image/heif': ['.heif'] }}
        onDrop={handleDrop}
      />
    </div>
  );
};

export default PhotoField;
